import React from 'react';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Box,
  Badge,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Receipt as InvoiceIcon,
  AccountBalance as ABOIcon,
  People as UsersIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useMobileLayout } from '../../hooks/useMobileLayout';
import { useTouchTarget, getTouchTargetStyles } from '../../hooks/useTouchTarget';

interface BottomNavigationProps {
  onNavigate: (path: string) => void;
}

const BottomNavigationComponent: React.FC<BottomNavigationProps> = ({ onNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const { invoices } = useSelector((state: RootState) => state.invoices);
  const { aboFiles } = useSelector((state: RootState) => state.abo);
  const { isSmallMobile } = useMobileLayout();
  const touchConfig = useTouchTarget();

  const getMenuItems = () => {
    const baseItems = [
      { 
        label: 'Nástěnka', 
        icon: <DashboardIcon />, 
        path: '/',
        badge: 0
      },
      { 
        label: 'Faktury', 
        icon: <InvoiceIcon />, 
        path: '/invoices',
        badge: invoices.filter(inv => inv.status === 'new').length
      },
      { 
        label: 'ABO', 
        icon: <ABOIcon />, 
        path: '/abo',
        badge: aboFiles.filter(f => f.status === 'generated').length
      },
    ];

    if (user && (user.role === 'admin' || user.role === 'superadmin')) {
      baseItems.push({ 
        label: 'Uživatelé', 
        icon: <UsersIcon />, 
        path: '/users',
        badge: 0
      });
    }

    return baseItems;
  };

  const menuItems = getMenuItems();

  const handleNavigation = (path: string) => {
    onNavigate(path);
    navigate(path);
  };

  const getCurrentValue = () => {
    const currentItem = menuItems.find(item => item.path === location.pathname);
    return currentItem ? currentItem.path : '/';
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 2000, // Higher z-index to ensure visibility
        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
        display: { xs: 'block', lg: 'none' }, // Show on tablets too
      }}
      elevation={8}
    >
      <BottomNavigation
        value={getCurrentValue()}
        onChange={(event, newValue) => {
          handleNavigation(newValue);
        }}
        sx={{
          height: isSmallMobile ? '70px' : '60px',
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            padding: '6px 0',
            color: 'text.secondary',
            '&.Mui-selected': {
              color: 'primary.main',
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: isSmallMobile ? '0.7rem' : '0.75rem',
              fontWeight: 500,
              marginTop: '4px',
            },
            '& .MuiSvgIcon-root': {
              fontSize: isSmallMobile ? '24px' : '22px',
            },
          },
        }}
      >
        {menuItems.map((item) => (
          <BottomNavigationAction
            key={item.path}
            label={item.label}
            value={item.path}
            icon={
              item.badge > 0 ? (
                <Badge
                  badgeContent={item.badge}
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: isSmallMobile ? '0.6rem' : '0.7rem',
                      minWidth: isSmallMobile ? '18px' : '16px',
                      height: isSmallMobile ? '18px' : '16px',
                    },
                  }}
                >
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )
            }
            sx={{
              ...getTouchTargetStyles(touchConfig),
              '&:active': {
                transform: `scale(${touchConfig.feedback.scale})`,
              },
            }}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
};

export default BottomNavigationComponent;
